import React, { useState, useContext } from 'react';
import { CurrencyContext } from '../../context/CurrencyContext';
import '../MortgageCalculator.css';

const SavingsCalculator: React.FC = () => {
  const [initialDeposit, setInitialDeposit] = useState<string>('10000');
  const [monthlyContribution, setMonthlyContribution] = useState<string>('500');
  const [annualInterestRate, setAnnualInterestRate] = useState<string>('5');
  const [years, setYears] = useState<string>('10');
  const [compoundingFrequency, setCompoundingFrequency] = useState<string>('12');
  const [result, setResult] = useState<{
    futureValue: number;
    totalContributions: number;
    totalInterest: number;
  } | null>(null);
  
  const { currencySymbol } = useContext(CurrencyContext);

  // Format currency for better readability
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const calculateSavings = () => {
    const P = parseFloat(initialDeposit);
    const PMT = parseFloat(monthlyContribution);
    const r = parseFloat(annualInterestRate) / 100;
    const t = parseFloat(years);
    const n = parseFloat(compoundingFrequency);
    
    const ratePerPeriod = r / n;
    const periods = n * t;
    
    const futureValue = P * Math.pow(1 + ratePerPeriod, periods) + 
                        PMT * (Math.pow(1 + ratePerPeriod, periods) - 1) / ratePerPeriod;
    
    const totalContributions = P + PMT * periods;
    const totalInterest = futureValue - totalContributions;
    
    setResult({
      futureValue,
      totalContributions,
      totalInterest
    });
  };

  return (
    <div className="calculator">
      <h2>Savings Calculator</h2>
      
      <div className="input-group">
        <label htmlFor="initialDeposit">Initial Deposit ({currencySymbol})</label>
        <input
          id="initialDeposit"
          type="number"
          value={initialDeposit}
          onChange={(e) => setInitialDeposit(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="monthlyContribution">Monthly Contribution ({currencySymbol})</label>
        <input
          id="monthlyContribution"
          type="number"
          value={monthlyContribution}
          onChange={(e) => setMonthlyContribution(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="annualInterestRate">Annual Interest Rate (%)</label>
        <input
          id="annualInterestRate"
          type="number"
          step="0.01"
          value={annualInterestRate}
          onChange={(e) => setAnnualInterestRate(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="years">Time Period (years)</label>
        <input
          id="years"
          type="number"
          value={years}
          onChange={(e) => setYears(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="compoundingFrequency">Compounding Frequency</label>
        <select
          id="compoundingFrequency"
          value={compoundingFrequency}
          onChange={(e) => setCompoundingFrequency(e.target.value)}
        >
          <option value="1">Annually</option>
          <option value="2">Semi-annually</option>
          <option value="4">Quarterly</option>
          <option value="12">Monthly</option>
          <option value="365">Daily</option>
        </select>
      </div>
      
      <button onClick={calculateSavings}>Calculate Future Value</button>
      
      {result && (
        <div className="result">
          <h3>Final Balance After {years} Years</h3>
          <p>{currencySymbol}{formatCurrency(result.futureValue)}</p>
          
          <div className="result-breakdown">
            <div className="result-item">
              <span className="result-item-label">Initial Deposit:</span>
              <span className="result-item-value">{currencySymbol}{formatCurrency(parseFloat(initialDeposit))}</span>
            </div>
            <div className="result-item">
              <span className="result-item-label">Total Contributions:</span>
              <span className="result-item-value">{currencySymbol}{formatCurrency(result.totalContributions)}</span>
            </div>
            <div className="result-item">
              <span className="result-item-label">Interest Earned:</span>
              <span className="result-item-value">{currencySymbol}{formatCurrency(result.totalInterest)}</span>
            </div>
            <div className="result-item">
              <span className="result-item-label">Monthly Contribution:</span>
              <span className="result-item-value">{currencySymbol}{formatCurrency(parseFloat(monthlyContribution))}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavingsCalculator;
